import { type FC } from "react";
import { Card, CardHeader, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  MetricCard,
  type MetricCardData,
} from "@/partner/shared/components/MetricCard";
import { formatCurrency } from "@/partner/features/products";
import { formatNumber } from "@/shared/utils/formatNumber";
import type { DashboardQueryType } from "@/types/graphql/partner.generated";

interface DashboardMetricsProps {
  metrics: NonNullable<DashboardQueryType["dashboard"]>["metrics"];
}

export const DashboardMetrics: FC<DashboardMetricsProps> = ({ metrics }) => {
  const { t } = useTranslation();

  const cards: MetricCardData[] = [
    {
      primary: {
        label: t("dashboard.metrics.views"),
        value: formatNumber(metrics.views),
        trend: metrics.viewsTrend,
        xs: 5,
      },
      secondary: {
        label: t("dashboard.metrics.visitors"),
        value: formatNumber(metrics.visitors),
        trend: metrics.visitorsTrend,
        xs: 7,
      },
      to: "/partner/analytics/views",
      xs: 12,
    },
    {
      primary: {
        label: t("dashboard.metrics.purchases"),
        value: formatNumber(metrics.purchases),
        trend: metrics.purchasesTrend,
        xs: 5,
      },
      secondary: {
        label: t("dashboard.metrics.revenue"),
        value: formatCurrency(metrics.revenue, metrics.revenueCurrency),
        trend: metrics.revenueTrend,
        xs: 7,
      },
      to: "/partner/analytics/sales",
      xs: 12,
    },
    {
      primary: {
        label: t("dashboard.metrics.users"),
        value: formatNumber(metrics.users),
        trend: metrics.usersTrend,
        xs: 12,
      },
      to: "/partner/analytics/users",
      xs: 12,
    },
  ];

  return (
    <Grid container spacing={1}>
      <Grid size={12}>
        <Card>
          <CardHeader
            title={t("dashboard.metrics.title")}
            subheader={t("dashboard.metrics.subheader")}
          />
        </Card>
      </Grid>
      {cards.map((card, index) => (
        <Grid key={index} size={12}>
          <MetricCard metric={card} />
        </Grid>
      ))}
    </Grid>
  );
};
